import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

export const SITE = 'ColorMark'
export const SITE_URL = 'https://colormark.org'
const DEFAULT_DESCRIPTION = 'Name and register your favorite color.'
const DEFAULT_IMAGE = `${SITE_URL}/images/og-default.png`

export interface Seo {
  /** Page title; the site name is appended. */
  title?: string
  description?: string
  /** Canonical path; the current path by default. */
  path?: string
  /** Absolute URL or site path of the preview image. */
  image?: string
  imageAlt?: string
  type?: 'website' | 'article' | 'product'
  /** Favicon override (href), for example a swatch in the page's color. */
  icon?: string
  noindex?: boolean
  /** Browser UI color on phones. */
  themeColor?: string
}

function upsert(selector: string, create: () => HTMLElement): HTMLElement {
  let el = document.head.querySelector<HTMLElement>(selector)
  if (!el) {
    el = create()
    document.head.appendChild(el)
  }
  return el
}

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  const el = upsert(`meta[${attr}="${key}"]`, () => {
    const m = document.createElement('meta')
    m.setAttribute(attr, key)
    return m
  })
  el.setAttribute('content', content)
}

function setLink(rel: string, href: string, type?: string) {
  const sel = type ? `link[rel="${rel}"][type="${type}"]` : `link[rel="${rel}"]`
  const el = upsert(sel, () => {
    const l = document.createElement('link')
    l.rel = rel
    if (type) l.type = type
    return l
  })
  el.setAttribute('href', href)
}

/**
 * Per-page SEO: title, description, canonical, Open Graph and Twitter cards,
 * favicon and theme color. Crawlers that run no JavaScript get the same
 * values for color pages from netlify/functions/color-page.mts; this keeps
 * the document in sync as people navigate.
 */
export function useSeo(seo: Seo) {
  const { pathname } = useLocation()
  const { title, description, path, image, imageAlt, type, icon, noindex, themeColor } = seo
  useEffect(() => {
    const fullTitle = title ? `${title} — ${SITE}` : SITE
    const desc = description ?? DEFAULT_DESCRIPTION
    const url = SITE_URL + (path ?? pathname)
    const img = image ? (image.startsWith('http') ? image : SITE_URL + image) : DEFAULT_IMAGE

    document.title = fullTitle
    setMeta('name', 'description', desc)
    setLink('canonical', url)
    setMeta('property', 'og:title', title ?? SITE)
    setMeta('property', 'og:description', desc)
    setMeta('property', 'og:url', url)
    setMeta('property', 'og:type', type ?? 'website')
    setMeta('property', 'og:image', img)
    setMeta('property', 'og:image:alt', imageAlt ?? (title ?? SITE))
    setMeta('name', 'twitter:title', title ?? SITE)
    setMeta('name', 'twitter:description', desc)
    setMeta('name', 'twitter:image', img)
    setMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large')
    setMeta('name', 'theme-color', themeColor ?? '#000000')
    setLink('icon', icon ?? '/favicon.svg', 'image/svg+xml')
  }, [title, description, path, pathname, image, imageAlt, type, icon, noindex, themeColor])
}

/** Title and description only; see useSeo for the rest. */
export function usePageTitle(title?: string, description?: string) {
  useSeo({ title, description })
}
